import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

/**
 * Connexion par email et mot de passe, via Firebase Auth.
 * Les comptes sont les mêmes que ceux de l'application mobile.
 *
 * L'inscription vit sur sa propre page : elle passe par un parcours OTP
 * en plusieurs étapes (voir RegisterPage).
 */
const inputClass =
  'w-full pl-10 pr-4 py-3 rounded-xl bg-[#0a0a0a] border border-[#252525] text-white placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-transparent';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, resetPassword } = useAuth();

  // Après vérification WhatsApp d'un numéro connu, l'email est pré-rempli
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const { success, profile } = await login(email, password);
    setLoading(false);
    if (!success) return;

    // Le profil métier peut manquer si l'inscription a été interrompue
    if (!profile) {
      navigate('/register');
      return;
    }
    navigate(['smart', 'livreur'].includes(profile.role) ? '/smart/dashboard' : '/dashboard');
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error('Entrez d\'abord votre adresse email');
      return;
    }
    await resetPassword(email);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] py-12 px-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#F97316]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#F97316]/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-[#111111] rounded-2xl border border-[#252525] shadow-2xl p-8 relative z-10"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F97316]/10 rounded-2xl mb-4">
            <LockClosedIcon className="w-8 h-8 text-[#F97316]" />
          </div>
          <h2 className="text-3xl font-display font-bold text-white">Connexion</h2>
          <p className="text-[#AAAAAA] mt-2">Bon retour parmi nous</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Email</label>
            <div className="relative">
              <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#666666]" />
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className={inputClass}
                placeholder="votre@email.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#CCCCCC] mb-2">Mot de passe</label>
            <div className="relative">
              <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#666666]" />
              <input
                type="password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className={inputClass}
                placeholder="••••••••"
              />
            </div>
          </div>

          <div className="text-right">
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-sm text-[#F97316] hover:underline"
            >
              Mot de passe oublié ?
            </button>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading}
            className="w-full bg-[#F97316] text-white py-3 rounded-xl font-semibold text-lg hover:bg-[#e8630a] transition-all shadow-lg shadow-[#F97316]/25 disabled:opacity-50"
          >
            {loading ? 'Connexion…' : 'Se connecter'}
          </motion.button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-[#AAAAAA]">
            Pas encore de compte ?{' '}
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="text-[#F97316] font-semibold hover:underline"
            >
              S'inscrire
            </button>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
